import { useEffect, useRef } from "react";

/** The slice of a react-query result the search pickers need to recover. */
export interface RecoverableQuery {
	isError?: boolean;
	isFetching?: boolean;
	refetch?: () => unknown;
}

/**
 * Makes retyping a search recover from a failed request.
 *
 * react-query only fetches when the query key changes, and the key is the
 * DEBOUNCED term. Retyping the same text quickly never changes it, so a
 * search that failed (a 503 during an outage, say) stayed failed with no new
 * request, however often the admin typed it again. Call `markTyped()` from
 * the input's change handler: once the typing settles on the failed term, the
 * query is fetched again. A term that settles on a different key is left to
 * react-query, which fetches it as usual.
 */
export function useRefetchOnRetype(
	inputValue: string,
	debouncedValue: string,
	query: RecoverableQuery
): { markTyped: () => void } {
	const typedSinceError = useRef(false);
	const { isError, isFetching, refetch } = query;

	useEffect(() => {
		if (!typedSinceError.current) return;
		// Still typing: wait for the debounced term to catch up.
		if (inputValue !== debouncedValue) return;
		typedSinceError.current = false;
		if (isError && !isFetching) refetch?.();
	}, [inputValue, debouncedValue, isError, isFetching, refetch]);

	return {
		markTyped: () => {
			if (isError) typedSinceError.current = true;
		}
	};
}
